import React from 'react';
import { useHomeFocus } from '../HomeFocusProvider';
import { useShift } from './useShift';
import { useShiftFlow, type ShiftFlowValue } from './useShiftFlow';
import {
    shiftGateNotice,
    type ShiftGateNoticeContent,
    type ShiftHandover,
} from './shiftMeta';

export type ShiftActionButtonValue = {
    /** The open/close flow the button and `F10` start, and the modal renders. */
    flow: ShiftFlowValue;
    isOpen: boolean;
    handover: ShiftHandover | null;
    /** The shift button; home while no shift is open. */
    buttonRef: React.RefObject<HTMLButtonElement | null>;
    /** `GET /shifts/current`, or the sale point's handover check, is still being read. */
    isPending: boolean;
    /** Nothing to press: the status isn't known (loading or failed), or the handover guard holds opening. */
    isUnavailable: boolean;
    /** What `F10` says while unavailable; `null` while the status loads, when it does nothing. */
    unavailableNotice: ShiftGateNoticeContent | null;
};

/**
 * Nobody is using focus: it is on the page itself, still on the shift
 * button, or on an empty search nobody has typed into. Anywhere else (a
 * search with text, a product tile, the cart, a dialog) it stays put.
 */
const isFocusIdle = (shiftButton: HTMLElement | null): boolean => {
    const active = document.activeElement;
    return (
        active === null ||
        active === document.body ||
        active === shiftButton ||
        (active instanceof HTMLInputElement &&
            active.type === 'search' &&
            active.value === '')
    );
};

/**
 * The navbar shift control's state and focus. Until `GET /shifts/current`
 * has answered successfully, `isOpen` reads `false` whatever the server
 * holds, so the control stays unavailable and takes no focus (SEL-49):
 * pending while the read is in flight, disabled after it failed (the cart's
 * gate notice offers the retry).
 *
 * The button is the sale screen's home while no shift is open (bos-seller#47):
 * it claims home from search (`useHomeFocus`) for as long as that holds and
 * it can take focus, so every "go home" (`Esc`, a closed dialog, a pointer add)
 * lands on it. Each close of the shift dialog sends focus home. So do arrival
 * and every later move of home (the status first known, the handover check
 * cleared, the shift opened or closed elsewhere), but only while nobody is
 * using focus (`isFocusIdle`): a seller already typing, or in a dialog, keeps
 * it. Never while the shift dialog is open, so it can't pull focus out of
 * `ShiftModal`'s own focus trap.
 */
export const useShiftActionButton = (): ShiftActionButtonValue => {
    const { isOpen, statusRead, handover, canOpenShift } = useShift();
    const { claimFocusHome, focusHome } = useHomeFocus();
    const flow = useShiftFlow();
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    const isStatusKnown = statusRead.state === 'known';
    // A disabled button can't hold focus: while the handover guard holds
    // opening, home stays search.
    const isHome = isStatusKnown && !isOpen && canOpenShift;
    React.useEffect(() => {
        if (!isHome) {
            return;
        }
        return claimFocusHome(() => buttonRef.current?.focus());
    }, [isHome, claimFocusHome]);

    const { isModalOpen } = flow;
    const wasModalOpen = React.useRef(isModalOpen);
    React.useEffect(() => {
        if (wasModalOpen.current && !isModalOpen) {
            focusHome();
        }
        wasModalOpen.current = isModalOpen;
    }, [isModalOpen, focusHome]);

    React.useEffect(() => {
        if (!isStatusKnown || isModalOpen || !isFocusIdle(buttonRef.current)) {
            return;
        }
        focusHome();
    }, [isStatusKnown, isHome, isOpen, isModalOpen, focusHome]);

    return {
        flow,
        isOpen,
        handover,
        buttonRef,
        isPending:
            statusRead.state === 'loading' || handover?.state === 'checking',
        isUnavailable: !isStatusKnown || (!isOpen && !canOpenShift),
        unavailableNotice:
            statusRead.state === 'loading'
                ? null
                : shiftGateNotice({ statusRead, isOpen, handover }),
    };
};
